import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload, X, GripVertical, Star } from 'lucide-react';
import ImageWithFallback from '../common/ImageWithFallback';
import ImageCropModal from '../common/ImageCropModal';
import { uploadService } from '../../services/uploadService';

// Drag handle + imagem. Cada imagem vira um SortableImage.
// `id` é o URL (único o suficiente pro DndKit).
const SortableImage = ({ url, isPrimary, onRemove, onSetPrimary }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-square rounded-lg overflow-hidden bg-app-surface-hover border ${
        isPrimary ? 'border-primary ring-2 ring-primary/30' : 'border-app-border'
      }`}
    >
      <ImageWithFallback
        src={url}
        alt="Produto"
        className="w-full h-full object-cover transition-all duration-200 group-hover:object-contain group-hover:bg-white"
      />
      {isPrimary && (
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary text-white text-[10px] font-semibold">
          <Star size={10} fill="currentColor" /> Principal
        </span>
      )}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 w-8 h-8 bg-white/90 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        aria-label="Arrastar para reordenar"
      >
        <GripVertical size={14} />
      </button>
      {!isPrimary && (
        <button
          type="button"
          onClick={() => onSetPrimary(url)}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-white/90 hover:bg-white text-app-text rounded-md text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Definir como principal"
        >
          <Star size={12} /> Tornar principal
        </button>
      )}
      <button
        type="button"
        onClick={() => onRemove(url)}
        className="absolute top-2 right-2 w-8 h-8 bg-error text-white rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
        aria-label="Remover imagem"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const ProductImagesUploader = ({ images = [], onChange, maxCount = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  // Fila de arquivos esperando passar pelo crop modal — processados um a um.
  const [queue, setQueue] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = images.indexOf(active.id);
    const newIdx = images.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(images, oldIdx, newIdx));
  };

  const handleRemove = (url) => {
    onChange(images.filter((u) => u !== url));
  };

  const handleSetPrimary = (url) => {
    if (!images.includes(url) || images[0] === url) return;
    onChange([url, ...images.filter((u) => u !== url)]);
  };

  const handleFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError(null);
    // Respeita maxCount já no momento do pick (evita abrir modal pra fotos
    // que iam ser descartadas no .slice(maxCount)).
    const slots = Math.max(0, maxCount - images.length);
    setQueue(files.slice(0, slots));
    e.target.value = '';
  };

  const advanceQueue = () => setQueue((q) => q.slice(1));

  const handleCropConfirm = async (croppedFile) => {
    setUploading(true);
    setError(null);
    try {
      const { url } = await uploadService.uploadImage(croppedFile);
      onChange([...images, url].slice(0, maxCount));
      advanceQueue();
    } catch (err) {
      setError(err.message || 'Erro no upload');
      // Mantém o arquivo no topo da fila pra ele tentar de novo se quiser.
      // Mas fecha o modal — abrindo de novo na próxima tentativa parece
      // confuso. Optamos por descartar e mostrar o erro.
      advanceQueue();
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    advanceQueue();
  };

  const canAddMore = images.length < maxCount;

  return (
    <div>
      {error && (
        <div className="mb-3 p-3 bg-error/10 border border-error/30 text-error rounded-lg text-sm">
          {error}
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={images} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((url, idx) => (
              <SortableImage
                key={url}
                url={url}
                isPrimary={idx === 0}
                onRemove={handleRemove}
                onSetPrimary={handleSetPrimary}
              />
            ))}
            {canAddMore && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-app-border hover:border-primary hover:bg-primary/5 cursor-pointer flex flex-col items-center justify-center text-app-text-secondary transition-colors">
                <Upload size={24} />
                <span className="text-xs font-medium mt-2 px-2 text-center">
                  {uploading ? 'Enviando…' : 'Adicionar foto'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFile}
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {!images.length && !uploading && (
        <p className="mt-3 text-sm text-app-text-secondary">
          Adicione pelo menos uma foto. Arraste pra reordenar ou clique em "Tornar principal" pra escolher a foto da listagem.
        </p>
      )}

      {images.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs text-app-text-secondary">
          <span>
            {images.length}/{maxCount} fotos
          </span>
          {canAddMore && (
            <label className="inline-flex items-center gap-1 cursor-pointer hover:text-primary">
              <Upload size={14} />
              Adicionar mais
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFile}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      )}

      <ImageCropModal
        open={queue.length > 0}
        file={queue[0] || null}
        aspect={1}
        title={
          queue.length > 1
            ? `Ajustar imagem (${queue.length} no total)`
            : 'Ajustar imagem'
        }
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

export default ProductImagesUploader;
